<script setup lang="ts">
import { computed, onMounted, useAttrs } from "vue";
import { useI18n } from "vue-i18n";

import { upperLevelLinksFeatureSelected } from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import { getStickies } from "@/backend/read/BackendReader";
import { addSticky } from "@/backend/read/changeSticky";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import ListItemSubtitleSticky from "@/components/ui/ComplexList/ListItemSubtitleSticky.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import ScreenReaderMessage from "@/components/ui/a11y/ScreenReaderMessage.vue";
import type { LinkedSticky } from "@/model/card";
import type { LinkableItem } from "@/model/link";
import { isArtLevelWorkitem } from "@/model/stickyType";
import { linkableStickyHref } from "@/router/navigation";
import { useArtStore } from "@/store/art";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { linkableCardOf } from "@/store/helpers/link";
import {
  isARTWorkItem,
  isDependency,
  isObjective,
  isOtherItem,
  isStickyLink,
  isTeamWorkItem,
} from "@/store/helpers/linkableItems";
import { useObjectiveStore } from "@/store/objective";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useWorkModeStore } from "@/store/workMode";

import StatusInfo from "../StatusInfo.vue";
import StickerInfo from "../StickerInfo.vue";

const attrs = useAttrs();
defineOptions({ inheritAttrs: false });

const props = defineProps<{
  link: LinkableItem;
  linkAnnouncement?: string;
  showUpperLevelLinks?: boolean;
  canEdit?: boolean;
  showNestedLinks?: boolean;
  nested?: boolean;
}>();

const emit = defineEmits(["toggleLink"]);

const { t } = useI18n();
const searchMenuStore = useSearchMenuStore();

const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);

function toggleLink(item: LinkableItem) {
  emit("toggleLink", item);
}

const card = computed(() => useCardStore().cards[props.link.id]);

const childrenLinks = computed(() => {
  if (!isStickyLink(props.link) || !card.value) return [];

  const children = useObjectiveStore().childrenLinkedToCard(card.value);

  return children.map((child) => linkableCardOf(child, false));
});

onMounted(() => {
  if (card.value?.linkedStickies) {
    void loadLinkedStickiesFromOtherBoards(card.value.linkedStickies);
  }
});

async function loadLinkedStickiesFromOtherBoards(links: readonly LinkedSticky[]) {
  const missingIds = new Set<string>();

  links.forEach((link) => {
    if (!useCardStore().cards[link.stickyId]) {
      missingIds.add(link.stickyId);
    }
  });

  if (missingIds.size > 0) {
    const stickies = await getStickies([...missingIds]);
    stickies.forEach(addSticky);
  }
}

const showChildren = computed(() => !props.nested && props.showNestedLinks);

const showUpperLevelLinksInternal = computed(
  () =>
    props.showUpperLevelLinks &&
    props.link.kind === "sticky" &&
    isArtLevelWorkitem(props.link) &&
    !props.nested,
);

function isShowUpperLevelLinksActive(stickyId: string) {
  return (
    searchMenuStore.selectedUpperLevelLinks.type === "sticky" &&
    searchMenuStore.selectedUpperLevelLinks.stickyId === stickyId
  );
}

const objectiveArtOrTeam = (boardId: string) => {
  const board = useBoardsStore().boardById(boardId);
  switch (board.type) {
    case "program":
      return useArtStore().artById(board.artId)?.name || t("general.noArt");
    case "team":
      return board.team.name;
  }
};

const tooltip = computed(() =>
  props.link.linked
    ? t("linkModal.removeLink", { text: props.link.text })
    : t("linkModal.addLink", { text: props.link.text }),
);

function handleShowUpperLevelLinks() {
  const current = searchMenuStore.selectedUpperLevelLinks;
  const isCurrentItem = current.type === "sticky" && current.stickyId === props.link.id;

  if (isCurrentItem) {
    searchMenuStore.selectedUpperLevelLinks = { type: "none" };
    return;
  }

  searchMenuStore.selectedUpperLevelLinks = { type: "sticky", stickyId: props.link.id };
  trackEvent(upperLevelLinksFeatureSelected(props.link.id, "sticky_note"));
}
</script>

<template>
  <ListItem v-bind="attrs" class="list-item-link">
    <template #before>
      <!-- Type Dot / Objective Icon -->
      <SvgIcon v-if="isObjective(link)" name="board/objectives-team" height="20" width="20" />
      <TypeDot v-else size="medium" class="type-icon" :color="link.type.color" />
    </template>

    <!-- First Row -->
    <BaseTooltip position="bottom">
      <div class="text-wrapper">
        <div class="text">{{ link.text }}</div>
        <a
          v-if="isStickyLink(link)"
          target="_blank"
          :href="linkableStickyHref(link, 'enlarge')"
          class="external-icon"
          :aria-label="$t('linkModal.openSticky')"
        >
          <SvgIcon name="general/external-link" width="14" height="14" />
        </a>
      </div>
      <template #content>{{ link.text }}</template>
    </BaseTooltip>

    <!-- Second Row -->
    <template #subtitle>
      <ListItemSubtitleSticky>
        <!-- ART work item -->
        <template v-if="isARTWorkItem(link)">
          <StatusInfo
            v-if="link.status && isExecutionMode"
            :status="link.status"
            data-testid="art-work-item-status"
          />
          <div>{{ link.type.name }}</div>
          <div v-if="link.almId">{{ link.almId }}</div>
          <div v-if="link.team">{{ link.team.name }}</div>
          <div v-if="link.iteration" class="iteration" :title="link.iteration.name">
            {{ link.iteration.name }}
          </div>
          <StickerInfo v-if="link.flagType" :sticker="link.flagType" />
        </template>

        <!-- Dependency -->
        <template v-if="isDependency(link)">
          <div>{{ link.type.name }}</div>
          <div>
            <span v-if="link.precondTeam"> {{ link.precondTeam.name }} </span>
            <span v-else class="no-team">
              {{ $t("linkChip.noPrecondTeam") }}
            </span>
            <SvgIcon name="arrow/right" class="arrow-icon" width="10" height="100%" />
            <span v-if="link.dependTeam"> {{ link.dependTeam.name }} </span>
            <span v-else class="no-team">
              {{ $t("linkChip.noDependTeam") }}
            </span>
          </div>
          <div v-if="link.iteration" class="iteration" :title="link.iteration.name">
            {{ link.iteration.name }}
          </div>
          <StickerInfo v-if="link.flagType" :sticker="link.flagType" />
        </template>

        <!-- Team work item -->
        <template v-if="isTeamWorkItem(link)">
          <StatusInfo
            v-if="link.status && isExecutionMode"
            :status="link.status"
            data-testid="team-work-item-status"
          />
          <div>{{ link.type.name }}</div>
          <div v-if="!isExecutionMode && link.points">
            {{ $t("linkChip.storyPoints", { points: link.points }) }}
          </div>
          <div v-if="link.almId">{{ link.almId }}</div>
          <div v-if="link.team">{{ link.team.name }}</div>
          <div v-if="link.iteration" class="iteration" :title="link.iteration.name">
            {{ link.iteration.name }}
          </div>
          <StickerInfo v-if="link.flagType" :sticker="link.flagType" />
        </template>

        <!-- Objective -->
        <template v-if="isObjective(link)">
          <div v-if="link.type === 'committed'">
            {{ $t("objectives.committed") }}
          </div>
          <div v-else>
            {{ $t("objectives.uncommitted") }}
          </div>
          <div>
            {{ objectiveArtOrTeam(link.boardId) }}
          </div>
        </template>

        <!-- Other -->
        <template v-if="isOtherItem(link)">
          <div>{{ link.type.name }}</div>
          <div v-if="link.team">{{ link.team.name }}</div>
          <div v-if="link.iteration" class="iteration" :title="link.iteration.name">
            {{ link.iteration.name }}
          </div>
          <StickerInfo v-if="link.flagType" :sticker="link.flagType" />
        </template>
      </ListItemSubtitleSticky>
    </template>

    <!-- Link Button -->
    <template #after>
      <div class="action-buttons">
        <IconButton
          v-if="showUpperLevelLinksInternal"
          :id="'upper-level-links-button-' + link.id"
          class="upper-level-links-button"
          :key-override="'upper-level-links-button-' + link.id"
          variant="ghost"
          :activated="isShowUpperLevelLinksActive(link.id)"
          icon="general/external-links"
          data-testid="upper-level-links-button"
          :text="t('label.showLinkedWorkItems')"
          @click="handleShowUpperLevelLinks"
        />
        <IconButton
          v-if="!nested"
          :id="'link-button-' + link.id"
          :key-override="'link-button-' + link.id"
          variant="ghost"
          :activated="link.linked"
          icon="general/link-remove"
          data-testid="toggle-link-button"
          :aria-label="tooltip"
          :text="tooltip"
          :disabled="!canEdit"
          @click="toggleLink(link)"
        />
      </div>
      <ScreenReaderMessage aria-live="polite">
        {{ linkAnnouncement }}
      </ScreenReaderMessage>
    </template>
  </ListItem>

  <div v-if="showChildren" class="nested-links">
    <ListItemLink
      v-for="childLink in childrenLinks"
      :key="'child-link-' + childLink.id + '-of-parent-' + link.id"
      :link="childLink"
      nested
      show-nested-links
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";

:deep(.list-item) {
  padding: 6px 16px;
}

.list-item-link {
  .text-wrapper {
    position: relative;
    padding-right: 26px;

    .text {
      @include utils.ellipsis;
    }

    .external-icon {
      display: none;
      cursor: pointer;
      position: absolute;
      right: 8px;
      top: 2px;
    }

    &:hover {
      .external-icon {
        display: block;
      }
    }
  }

  .no-team {
    border: 1px dashed colors.$text-subtle;
    border-radius: 8px;
    padding: 0 4px;
  }

  .arrow-icon {
    margin-left: 8px;
    margin-right: 8px;
  }

  // Make the icon appear centered with the text of the item
  .type-icon {
    margin-top: 1px;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
  }

  .subtitle > .subtitle {
    .iteration {
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      white-space: nowrap;
    }
  }
}

.nested-links {
  margin-left: 28px;
}
</style>
